import { useEffect, useRef } from "react"
import { useTranslation } from "react-i18next"
import { Paperclip, X } from "lucide-react"

import { Button, Badge } from "@f2e/ui/components/base"
import { cn } from "@f2e/ui/lib/utils"

import { useToast } from "@/hooks"
import { useUploadCopilotFileApi } from "@/hooks/api/copilot"
import { getUserFacingApiErrorMessage } from "@/lib/apiErrors"

import type { CopilotFile } from "./types"
import { processAndUploadCopilotFiles } from "./utils"

interface CopilotFileUploadProps {
  className?: string
  files?: CopilotFile[]
  disabled?: boolean
  onFilesChange?: (
    files: CopilotFile[] | ((prevFiles: CopilotFile[]) => CopilotFile[])
  ) => void
}

const CopilotFileUpload = ({
  className,
  files = [],
  disabled = false,
  onFilesChange,
}: CopilotFileUploadProps) => {
  const { t } = useTranslation()
  const { alertToast } = useToast()

  const fileInputRef = useRef<HTMLInputElement>(null)
  const {
    error: uploadError,
    data: uploadResp,
    fetchFn: uploadCopilotFile,
  } = useUploadCopilotFileApi()

  // Show API error via toast using hook-provided response
  useEffect(() => {
    if (!uploadError) return
    alertToast({
      title: t("common.fileUpload.error.title"),
      description: getUserFacingApiErrorMessage(
        uploadError,
        t,
        uploadResp?.message || t("common.fileUpload.error.defaultMessage")
      ),
    })
  }, [uploadError, uploadResp, alertToast, t])

  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const selectedFiles = Array.from(event.target.files || [])
    if (selectedFiles.length === 0) return

    await processAndUploadCopilotFiles({
      files: selectedFiles,
      t,
      alertToast,
      upload: uploadCopilotFile,
      onAdd: (newFile) =>
        onFilesChange?.((prev) => {
          return [...prev, newFile]
        }),
    })

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }
  }

  const handleRemoveFile = (fileId: string) => {
    onFilesChange?.(files.filter((f) => f.id !== fileId))
  }

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return `0 ${t("common.copilot.fileUpload.units.bytes")}`
    const k = 1024
    const sizes = [
      t("common.copilot.fileUpload.units.bytes"),
      t("common.copilot.fileUpload.units.kb"),
      t("common.copilot.fileUpload.units.mb"),
      t("common.copilot.fileUpload.units.gb"),
    ]
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i]
  }

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      {files.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {files.map((file) => (
            <Badge
              key={file.id}
              variant="secondary"
              className="flex items-center gap-2 pr-1"
            >
              <span className="truncate max-w-[200px]">
                {file.name} ({formatFileSize(file.size)})
              </span>
              <Button
                variant="ghost"
                size="icon"
                className="h-4 w-4 rounded-full hover:bg-muted"
                onClick={() => handleRemoveFile(file.id)}
                disabled={disabled}
              >
                <X className="h-3 w-3" />
              </Button>
            </Badge>
          ))}
        </div>
      )}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={handleFileSelect}
        disabled={disabled}
      />
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8 rounded-full p-0 shrink-0"
        onClick={() => fileInputRef.current?.click()}
        disabled={disabled}
        aria-label={t("common.copilot.fileUpload.uploadAria")}
      >
        <Paperclip className="h-4 w-4" />
      </Button>
    </div>
  )
}

export default CopilotFileUpload
